import React, { useEffect, useState } from 'react';
import { Product } from '../../../types';
import { includes } from '../../../helpers';
import * as S from './styles';

type OwnProps = {
  products: Product[];
  totalActiveDots: number;
  totalVisibleDots: number;
  dotWidthVal: number;
  positionIndexes: number[];
};

const Dots = ({
  products,
  totalActiveDots,
  totalVisibleDots,
  dotWidthVal,
  positionIndexes,
}: OwnProps) => {
  const showSmallDots = products.length > totalVisibleDots;

  const wrappersWidth =
    products.length < totalVisibleDots
      ? products.length * dotWidthVal
      : totalVisibleDots * dotWidthVal;

  const [activeIndexes, setActiveIndexes] = useState<number[]>(
    [...Array(totalActiveDots)].map((val, index) => index)
  );
  const [smallIndexRight, setSmallIndexRight] = useState<number | null>(
    showSmallDots ? totalVisibleDots - 1 : null
  );
  const [smallIndexLeft, setSmallIndexLeft] = useState<number | null>(null);
  const [dotsTranslateX, setDotsTranslateX] = useState(0);

  const handleLeftDirection = () => {
    // Prevents position to change past products.length
    if (includes(activeIndexes, 0)) return;
    const newActiveIndexes = activeIndexes.map(i => i - 1);

    // Active index is the same as smallIndexRight
    if (showSmallDots && includes(newActiveIndexes, smallIndexLeft)) {
      // If active index is not at the end, move the smallIndexLeft
      if (!includes(newActiveIndexes, 0)) {
        setSmallIndexRight(
          smallIndexRight ? smallIndexRight - 1 : products.length - 2
        );
        setDotsTranslateX(dotsTranslateX + dotWidthVal);
      }

      if (includes(newActiveIndexes, 1)) {
        // Remove small right dot on second last index
        setSmallIndexLeft(null);
      } else {
        // Move small left dot backwards
        setSmallIndexLeft(smallIndexLeft ? smallIndexLeft - 1 : null);
      }
    }

    setActiveIndexes(newActiveIndexes);
  };

  const handleRightDirection = () => {
    // Prevents position to change past products.length
    if (includes(activeIndexes, products.length - 1)) return;
    const newActiveIndexes = activeIndexes.map(i => i + 1);

    // Active index is the same as smallIndexRight
    if (showSmallDots && includes(newActiveIndexes, smallIndexRight)) {
      // If active index is not at the end, move the smallIndexLeft
      if (!includes(newActiveIndexes, products.length - 1)) {
        setSmallIndexLeft(smallIndexLeft ? smallIndexLeft + 1 : 1);
        setDotsTranslateX(dotsTranslateX - dotWidthVal);
      }

      if (includes(newActiveIndexes, products.length - 2)) {
        // Remove small right dot on second last index
        setSmallIndexRight(null);
      } else {
        // Move small right dot forwards
        setSmallIndexRight(smallIndexRight ? smallIndexRight + 1 : null);
      }
    }

    setActiveIndexes(newActiveIndexes);
  };

  // Sets direction when position indexes changes
  useEffect(() => {
    if (
      positionIndexes &&
      JSON.stringify(activeIndexes) !== JSON.stringify(positionIndexes)
    ) {
      const directionRight = positionIndexes[0] > activeIndexes[0];
      const jumps = directionRight
        ? positionIndexes[0] - activeIndexes[0]
        : activeIndexes[0] - positionIndexes[0];

      [...Array(jumps)].forEach(() =>
        directionRight ? handleRightDirection() : handleLeftDirection()
      );
    }
  });

  return (
    <S.DotsWrapper width={wrappersWidth}>
      <S.Dots translateX={dotsTranslateX}>
        {products.map((product, index) => {
          const highlighted = includes(activeIndexes, index);
          const small = index === smallIndexRight || index === smallIndexLeft;
          return (
            <S.DotListItem width={dotWidthVal} key={product.id}>
              <S.Dot highlighted={highlighted} small={small} />
            </S.DotListItem>
          );
        })}
      </S.Dots>
    </S.DotsWrapper>
  );
};

export default Dots;
